import { useMemo, useState, useEffect, useRef, useCallback } from 'react'
import { ChevronDown, Clock, Users } from 'lucide-react'
import type { Booking } from '@/lib/types/booking'
import type { OpeningHour } from '@/lib/types/openingHour'
import type { Area } from '@/lib/types/area'
import { STATUS_CONFIG } from '@/lib/utils/booking'

interface BookingTimelineProps {
  bookings: Booking[]
  areas: Area[]
  openingHours?: OpeningHour[]
  date?: string
  onBookingClick: (documentId: string) => void
  onCreateBooking?: (tableDocumentId: string, time: string, duration: number) => void
  clearPreviewSignal?: number
}

const DEFAULT_START = 8
const DEFAULT_END   = 23
const COL_W         = 100  // px per hour
const ROW_H         = 40   // px per table row
const AREA_H        = 36   // px for area header row
const HEADER_H      = 32   // px for hour header

function getHoursRange(openingHours: OpeningHour[] | undefined, date: string | undefined): number[] {
  if (!openingHours?.length || !date) {
    return Array.from({ length: DEFAULT_END - DEFAULT_START }, (_, i) => i + DEFAULT_START)
  }
  const jsDay = new Date(date + 'T12:00:00').getDay()
  const storedDay = jsDay === 0 ? 7 : jsDay
  const matching = openingHours.filter((oh) => !oh.special && oh.day === storedDay)
  if (!matching.length) {
    return Array.from({ length: DEFAULT_END - DEFAULT_START }, (_, i) => i + DEFAULT_START)
  }
  const startHour = Math.floor(Math.min(...matching.map((oh) => Math.floor(oh.open  / 100))))
  const endHour   = Math.ceil( Math.max(...matching.map((oh) => Math.ceil( oh.close / 100))))
  const from = Math.max(0,  startHour - 1)
  const to   = Math.min(24, endHour   + 1)
  return Array.from({ length: to - from }, (_, i) => i + from)
}

function minuteToTime(minute: number): string {
  const h = Math.floor(minute / 60)
  const m = minute % 60
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
}

function bookingStartMinute(booking: Booking, firstHour: number) {
  const [hStr, mStr] = booking.time.split(':')
  return (Number(hStr) - firstHour) * 60 + Number(mStr)
}

interface DragPreview {
  tableId: number
  startMinute: number
  endMinute: number
}

export function BookingTimeline({ bookings, areas, openingHours, date, onBookingClick, onCreateBooking, clearPreviewSignal }: BookingTimelineProps) {
  const [collapsed, setCollapsed] = useState<Set<number>>(new Set())

  const toggle = (areaId: number) =>
    setCollapsed((prev) => {
      const next = new Set(prev)
      next.has(areaId) ? next.delete(areaId) : next.add(areaId)
      return next
    })

  const HOURS = useMemo(() => getHoursRange(openingHours, date), [openingHours, date])
  const TOTAL_MINUTES = HOURS.length * 60

  const areasWithTables = useMemo(
    () => areas.filter((a) => (a.tables ?? []).length > 0),
    [areas],
  )

  // Current time (dev: override with ?mockTime=HH:MM)
  const mockTime = new URLSearchParams(window.location.search).get('mockTime')
  const [now, setNow] = useState(() => {
    if (mockTime) {
      const [h, m] = mockTime.split(':').map(Number)
      const d = new Date(); d.setHours(h, m, 0, 0); return d
    }
    return new Date()
  })
  useEffect(() => {
    if (mockTime) return
    const id = setInterval(() => setNow(new Date()), 60_000)
    return () => clearInterval(id)
  }, [mockTime])
  const nowMinutes = now.getHours() * 60 + now.getMinutes()
  const nowOffset  = nowMinutes - HOURS[0] * 60
  const nowPx      = (nowOffset / TOTAL_MINUTES) * (HOURS.length * COL_W)
  const localDateStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`
  const showNow    = !date || date === localDateStr
  const nowVisible = showNow && nowOffset >= 0 && nowOffset <= TOTAL_MINUTES

  // Scroll refs
  const scrollRef  = useRef<HTMLDivElement>(null)

  // Auto-scroll to center the current time line on mount
  useEffect(() => {
    if (!nowVisible || !scrollRef.current) return
    const t = setTimeout(() => {
      if (!scrollRef.current) return
      const container = scrollRef.current
      const offset = nowPx - container.clientWidth / 2
      container.scrollLeft = Math.max(0, offset)
    }, 150)
    return () => clearTimeout(t)
  }, [nowVisible, nowPx])

  // Drag-to-scroll on header
  const dragging   = useRef(false)
  const startX     = useRef(0)
  const scrollLeft = useRef(0)

  const onMouseDown = useCallback((e: React.MouseEvent) => {
    dragging.current   = true
    startX.current     = e.pageX - (scrollRef.current?.offsetLeft ?? 0)
    scrollLeft.current = scrollRef.current?.scrollLeft ?? 0
  }, [])
  const onMouseMove = useCallback((e: React.MouseEvent) => {
    if (!dragging.current || !scrollRef.current) return
    e.preventDefault()
    const walk = (e.pageX - scrollRef.current.offsetLeft) - startX.current
    scrollRef.current.scrollLeft = scrollLeft.current - walk
  }, [])
  const onMouseUp = useCallback(() => { dragging.current = false }, [])

  // ── Drag-to-create ──────────────────────────────────────────────────────────
  // Use refs for drag state to avoid stale closures in window listeners
  const hoursRef          = useRef(HOURS)
  const onCreateRef       = useRef(onCreateBooking)
  useEffect(() => { hoursRef.current = HOURS }, [HOURS])
  useEffect(() => { onCreateRef.current = onCreateBooking }, [onCreateBooking])

  const dragData   = useRef<{ tableId: number; tableDocId: string; startMinute: number } | null>(null)
  const dragEndMin = useRef<number>(0)
  const [dragPreview, setDragPreview] = useState<DragPreview | null>(null)

  useEffect(() => { if (clearPreviewSignal) setDragPreview(null) }, [clearPreviewSignal])

  const sortedBookings = useMemo(
    () => [...bookings].sort((a, b) => a.time.localeCompare(b.time)),
    [bookings],
  )

  const clientXToMinute = useCallback((clientX: number): number => {
    if (!scrollRef.current) return 0
    const rect = scrollRef.current.getBoundingClientRect()
    const x = clientX - rect.left + scrollRef.current.scrollLeft
    const raw = (x / COL_W) * 60 + hoursRef.current[0] * 60
    return Math.round(raw / 15) * 15
  }, [])

  const onRowMouseDown = useCallback((e: React.MouseEvent, tableId: number, tableDocId: string) => {
    // Don't intercept clicks on existing booking blocks
    if ((e.target as HTMLElement).closest('[data-booking]')) return
    if (!onCreateRef.current) return
    e.preventDefault()
    const startMinute = clientXToMinute(e.clientX)
    dragData.current  = { tableId, tableDocId, startMinute }
    dragEndMin.current = startMinute + 120
    setDragPreview({ tableId, startMinute, endMinute: startMinute + 120 })
  }, [clientXToMinute])

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!dragData.current) return
      const cur = clientXToMinute(e.clientX)
      // Offset by 120 min so the cursor acts as if it's at the right edge of the 2h block:
      // moving left shortens, moving right extends
      const end = Math.max(dragData.current.startMinute + 15, Math.min(cur + 120, 1440))
      dragEndMin.current = end
      setDragPreview({ tableId: dragData.current.tableId, startMinute: dragData.current.startMinute, endMinute: end })
    }
    const onUp = () => {
      if (!dragData.current) return
      const { tableDocId, startMinute } = dragData.current
      const duration = dragEndMin.current - startMinute
      dragData.current = null
      if (duration >= 15 && onCreateRef.current) {
        onCreateRef.current(tableDocId, minuteToTime(startMinute), duration)
      } else {
        setDragPreview(null)
      }
    }
    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseup', onUp)
    return () => {
      window.removeEventListener('mousemove', onMove)
      window.removeEventListener('mouseup', onUp)
    }
  }, [clientXToMinute])

  if (areasWithTables.length === 0) {
    return (
      <div className="flex rounded-[var(--radius-md)] border border-[var(--color-border-subtle)] overflow-hidden">
        <div className="shrink-0 z-10 bg-[var(--color-bg-surface)]" style={{ width: 176 }}>
          <div
            className="flex items-center px-3 border-b border-r border-[var(--color-border-subtle)] bg-[var(--color-bg-surface)] text-xs font-medium text-[var(--color-text-muted)]"
            style={{ height: HEADER_H }}
          >
            Reservations
          </div>
          {sortedBookings.length === 0 ? (
            <div className="flex h-28 items-center px-3 border-r border-[var(--color-border-subtle)] text-xs text-[var(--color-text-muted)]">
              No reservations for this view.
            </div>
          ) : (
            sortedBookings.map((booking, i) => (
              <button
                key={booking.documentId}
                onClick={() => onBookingClick(booking.documentId)}
                className={`flex w-full flex-col justify-center px-3 text-left border-r border-[var(--color-border-subtle)] hover:bg-[var(--color-bg-elevated)] transition-colors ${i < sortedBookings.length - 1 ? 'border-b' : ''}`}
                style={{ height: ROW_H }}
              >
                <span className="text-xs font-medium text-[var(--color-text-primary)] truncate">
                  {booking.guest?.name ?? 'Walk-in'}
                </span>
                <span className="flex items-center gap-2 text-[10px] text-[var(--color-text-muted)]">
                  <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{booking.time}</span>
                  <span className="flex items-center gap-1"><Users className="h-3 w-3" />{booking.people}</span>
                </span>
              </button>
            ))
          )}
        </div>

        <div ref={scrollRef} className="flex-1 overflow-x-auto">
          <div className="relative" style={{ width: HOURS.length * COL_W }}>
            <div
              id="timeline-header"
              className="flex border-b border-[var(--color-border-subtle)] bg-[var(--color-bg-surface)] sticky top-0 z-10 select-none cursor-grab active:cursor-grabbing overflow-hidden"
              style={{ height: HEADER_H }}
              onMouseDown={onMouseDown}
              onMouseMove={onMouseMove}
              onMouseUp={onMouseUp}
              onMouseLeave={onMouseUp}
            >
              {HOURS.map((h) => (
                <div
                  key={h}
                  className="shrink-0 flex items-center justify-center text-xs text-[var(--color-text-muted)] border-r border-[var(--color-border-subtle)]"
                  style={{ width: COL_W }}
                >
                  {String(h).padStart(2, '0')}:00
                </div>
              ))}
            </div>

            {sortedBookings.length === 0 ? (
              <div className="h-28 bg-[var(--color-bg-base)]" />
            ) : (
              sortedBookings.map((booking, i) => {
                const startMinute = bookingStartMinute(booking, HOURS[0])
                const leftPct = (startMinute / TOTAL_MINUTES) * 100
                const widthPct = (booking.duration / TOTAL_MINUTES) * 100
                const cfg = STATUS_CONFIG[booking.status]
                return (
                  <div
                    key={booking.documentId}
                    className={`relative flex hover:bg-[var(--color-bg-elevated)]/30 ${i < sortedBookings.length - 1 ? 'border-b border-[var(--color-border-subtle)]' : ''}`}
                    style={{ height: ROW_H }}
                  >
                    {HOURS.map((h) => (
                      <div
                        key={h}
                        className="shrink-0 h-full border-r border-[var(--color-border-subtle)]/40 relative"
                        style={{ width: COL_W }}
                      >
                        <div className="absolute w-px bg-[var(--color-border-subtle)]/25" style={{ left: '25%', top: '30%', bottom: '30%' }} />
                        <div className="absolute w-px bg-[var(--color-border-subtle)]/40" style={{ left: '50%', top: '20%', bottom: '20%' }} />
                        <div className="absolute w-px bg-[var(--color-border-subtle)]/25" style={{ left: '75%', top: '30%', bottom: '30%' }} />
                      </div>
                    ))}
                    <button
                      onClick={() => onBookingClick(booking.documentId)}
                      className={`absolute top-1 h-8 rounded-[var(--radius-sm)] px-2 text-[10px] font-medium truncate ${cfg.color} ${cfg.textColor} hover:opacity-80 transition-opacity cursor-pointer`}
                      style={{ left: `${Math.max(leftPct, 0)}%`, width: `${Math.max(widthPct, 8)}%` }}
                      title={`${booking.guest?.name ?? 'Walk-in'} (${booking.people}p)`}
                    >
                      {booking.time} · {booking.guest?.name ?? 'Walk-in'}
                    </button>
                  </div>
                )
              })
            )}

            {nowVisible && (
              <div
                className="absolute top-0 bottom-0 z-20 pointer-events-none"
                style={{ left: nowPx }}
              >
                <div className="absolute -top-1.5 -translate-x-1/2 h-3 w-3 rounded-full bg-red-500 shadow-sm" />
                <div className="absolute top-0 bottom-0 left-0 w-px bg-red-500/70" />
                <div className="absolute top-1 -translate-x-1/2 text-[9px] font-semibold text-white bg-red-500 px-2 py-0.5 rounded-full whitespace-nowrap">
                  {String(now.getHours()).padStart(2, '0')}:{String(now.getMinutes()).padStart(2, '0')}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="flex rounded-[var(--radius-md)] border border-[var(--color-border-subtle)] overflow-hidden">

      {/* ── LEFT: frozen table-name column ── */}
      <div className="shrink-0 z-10 bg-[var(--color-bg-surface)]" style={{ width: 144 }}>

        {/* Header cell */}
        <div
          className="flex items-center px-3 border-b border-r border-[var(--color-border-subtle)] bg-[var(--color-bg-surface)] text-xs font-medium text-[var(--color-text-muted)]"
          style={{ height: HEADER_H }}
        >
          Table
        </div>

        {/* Area groups */}
        {areasWithTables.map((area) => {
          const isCollapsed = collapsed.has(area.id)
          const tables = area.tables ?? []
          return (
            <div key={area.id}>
              {/* Area label */}
              <button
                onClick={() => toggle(area.id)}
                className="flex items-center gap-2 w-full px-3 border-b border-r border-[var(--color-border-subtle)] bg-[var(--color-bg-elevated)] hover:bg-[var(--color-bg-overlay)] transition-colors cursor-pointer"
                style={{ height: AREA_H }}
              >
                <ChevronDown
                  className={`h-3.5 w-3.5 text-[var(--color-text-muted)] transition-transform shrink-0 ${isCollapsed ? '-rotate-90' : ''}`}
                />
                <span className="text-xs font-semibold text-[var(--color-text-primary)] truncate">
                  {area.name}
                </span>
              </button>

              {/* Table labels */}
              {!isCollapsed && tables.map((table, i) => (
                <div
                  key={table.id}
                  className={`flex flex-col justify-center px-3 border-r border-[var(--color-border-subtle)] ${i < tables.length - 1 ? 'border-b' : ''}`}
                  style={{ height: ROW_H }}
                >
                  <div className="text-xs font-medium text-[var(--color-text-primary)] truncate">{table.name}</div>
                  <div className="text-[10px] text-[var(--color-text-muted)]">{table.seatsMin}–{table.seatsMax} seats</div>
                </div>
              ))}
            </div>
          )
        })}
      </div>

      {/* ── RIGHT: scrollable hours + booking grid ── */}
      <div ref={scrollRef} className="flex-1 overflow-x-auto">
        <div className="relative" style={{ width: HOURS.length * COL_W }}>

          {/* Hour header — drag to scroll */}
          <div
            id="timeline-header"
            className="flex border-b border-[var(--color-border-subtle)] bg-[var(--color-bg-surface)] sticky top-0 z-10 select-none cursor-grab active:cursor-grabbing overflow-hidden"
            style={{ height: HEADER_H }}
            onMouseDown={onMouseDown}
            onMouseMove={onMouseMove}
            onMouseUp={onMouseUp}
            onMouseLeave={onMouseUp}
          >
            {HOURS.map((h) => (
              <div
                key={h}
                className="shrink-0 flex items-center justify-center text-xs text-[var(--color-text-muted)] border-r border-[var(--color-border-subtle)]"
                style={{ width: COL_W }}
              >
                {String(h).padStart(2, '0')}:00
              </div>
            ))}
          </div>

          {/* Area groups */}
          {areasWithTables.map((area) => {
            const isCollapsed = collapsed.has(area.id)
            const tables = area.tables ?? []
            const areaBookingCount = bookings.filter((b) =>
              b.bookingTables?.some((bt) => tables.some((t) => t.id === bt.table?.id)),
            ).length

            return (
              <div key={area.id}>
                {/* Area spacer row */}
                <div
                  className="flex items-center px-3 border-b border-[var(--color-border-subtle)] bg-[var(--color-bg-elevated)] text-[10px] text-[var(--color-text-muted)]"
                  style={{ height: AREA_H }}
                >
                  {tables.length} {tables.length === 1 ? 'table' : 'tables'}
                  {areaBookingCount > 0 && ` · ${areaBookingCount} booking${areaBookingCount !== 1 ? 's' : ''}`}
                </div>

                {/* Table booking rows */}
                {!isCollapsed && tables.map((table, i) => {
                  const tableBookings = bookings.filter((b) =>
                    b.bookingTables?.some((bt) => bt.table?.id === table.id),
                  )
                  const preview = dragPreview?.tableId === table.id ? dragPreview : null
                  const previewLeft = preview ? ((preview.startMinute - HOURS[0] * 60) / TOTAL_MINUTES) * 100 : 0
                  const previewWidth = preview ? ((preview.endMinute - preview.startMinute) / TOTAL_MINUTES) * 100 : 0

                  return (
                    <div
                      key={table.id}
                      className={`relative flex ${onCreateBooking ? 'cursor-crosshair' : ''} hover:bg-[var(--color-bg-elevated)]/30 ${i < tables.length - 1 ? 'border-b border-[var(--color-border-subtle)]' : ''}`}
                      style={{ height: ROW_H }}
                      onMouseDown={(e) => onRowMouseDown(e, table.id, table.documentId)}
                    >
                      {/* Hour grid lines with quarter-hour ticks */}
                      {HOURS.map((h) => (
                        <div
                          key={h}
                          className="shrink-0 h-full border-r border-[var(--color-border-subtle)]/40 relative"
                          style={{ width: COL_W }}
                        >
                          {/* :15 — short, very faint */}
                          <div className="absolute w-px bg-[var(--color-border-subtle)]/25" style={{ left: '25%', top: '30%', bottom: '30%' }} />
                          {/* :30 — taller, slightly more visible */}
                          <div className="absolute w-px bg-[var(--color-border-subtle)]/40" style={{ left: '50%', top: '20%', bottom: '20%' }} />
                          {/* :45 — short, very faint */}
                          <div className="absolute w-px bg-[var(--color-border-subtle)]/25" style={{ left: '75%', top: '30%', bottom: '30%' }} />
                        </div>
                      ))}

                      {/* Drag preview block */}
                      {preview && (
                        <div
                          className="absolute top-1 h-8 rounded-[var(--radius-sm)] bg-indigo-500/20 border border-dashed border-indigo-400/50 pointer-events-none z-10 flex items-center justify-center overflow-hidden"
                          style={{ left: `${previewLeft}%`, width: `${Math.max(previewWidth, 1)}%` }}
                        >
                          <span className="text-[9px] font-semibold text-indigo-400 px-1 truncate">
                            {minuteToTime(preview.startMinute)} · {preview.endMinute - preview.startMinute}m
                          </span>
                        </div>
                      )}

                      {/* Booking blocks */}
                      {tableBookings.map((b) => {
                        const [hStr, mStr] = b.time.split(':')
                        const startMinute = (Number(hStr) - HOURS[0]) * 60 + Number(mStr)
                        const leftPct  = (startMinute / TOTAL_MINUTES) * 100
                        const widthPct = (b.duration  / TOTAL_MINUTES) * 100
                        const cfg = STATUS_CONFIG[b.status]
                        return (
                          <button
                            key={b.documentId}
                            data-booking
                            onClick={() => onBookingClick(b.documentId)}
                            className={`absolute top-1 h-8 rounded-[var(--radius-sm)] px-1.5 text-[10px] font-medium truncate ${cfg.color} ${cfg.textColor} hover:opacity-80 transition-opacity cursor-pointer`}
                            style={{ left: `${leftPct}%`, width: `${Math.max(widthPct, 2)}%` }}
                            title={`${b.guest?.name ?? 'Walk-in'} (${b.people}p)`}
                          >
                            {b.guest?.name?.split(' ')[0] ?? 'Walk-in'}
                          </button>
                        )
                      })}
                    </div>
                  )
                })}
              </div>
            )
          })}

          {/* Current time line */}
          {nowVisible && (
            <div
              className="absolute top-0 bottom-0 z-20 pointer-events-none"
              style={{ left: nowPx }}
            >
              <div className="absolute -top-1.5 -translate-x-1/2 h-3 w-3 rounded-full bg-red-500 shadow-sm" />
              <div className="absolute top-0 bottom-0 left-0 w-px bg-red-500/70" />
              <div className="absolute top-1 -translate-x-1/2 text-[9px] font-semibold text-white bg-red-500 px-2 py-0.5 rounded-full whitespace-nowrap">
                {String(now.getHours()).padStart(2, '0')}:{String(now.getMinutes()).padStart(2, '0')}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
